import React from 'react';
import { Card, Button, Space, Checkbox, Tooltip } from 'antd';
import { CheckOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { PendingItem } from '../lib/api';
import './DetectedItemCard.css';

interface DetectedItemCardProps {
  item: PendingItem;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onEdit?: (item: PendingItem) => void;
  onRemove?: (item: PendingItem) => void;
  onSimilarityClick?: (item: PendingItem) => void;
}

export const DetectedItemCard: React.FC<DetectedItemCardProps> = ({
  item,
  selected = false,
  onToggleSelect,
  onEdit,
  onRemove,
  onSimilarityClick,
}) => {
  const hasSimilarItems = item.similarity_info?.has_similar_items || false;
  const metaData = item.meta_data || {};
  
  const name = metaData.name || metaData.category || 'Clothing Item';
  const category = metaData.category_level_2 || metaData.category;
  const color = metaData.main_color || metaData.color;
  const style = metaData.style_keyword || metaData.style;

  const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  const adminKey = localStorage.getItem('admin_key');
  const imageUrl = adminKey
    ? `${apiBase}/admin/images/${item.image_id}?x_admin_key=${encodeURIComponent(adminKey)}`
    : `${apiBase}/images/${item.image_id}`;

  return (
    <Card
      className={`detected-item-card ${hasSimilarItems ? 'has-similar-items' : ''} ${!selected ? 'not-selected' : ''}`}
      size="small"
      style={{
        borderRadius: 16,
        border: hasSimilarItems ? '1px solid #ff9800' : '1px solid #e0e0e0',
        backgroundColor: hasSimilarItems ? '#fff3e0' : '#ffffff',
        height: '100%',
      }}
      bodyStyle={{ padding: 16 }}
    >
      <div style={{ display: 'flex', gap: 12 }}>
        {/* Selection Checkbox */}
        {onToggleSelect && (
          <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 4 }}>
            <Checkbox
              checked={selected}
              onChange={() => onToggleSelect(item.id)}
            />
          </div>
        )}

        {/* Image */}
        <div style={{ 
          width: 80, 
          height: 80, 
          flexShrink: 0, 
          borderRadius: 12, 
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <img
            src={imageUrl}
            alt={name}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain',
              display: 'block'
            }}
            onError={(e) => {
              e.currentTarget.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN";
            }}
          />
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 14, color: '#262626', marginBottom: 4 }}>
                {name}
              </div>
              {(item.confidence !== undefined || item.similarity_info?.confidence !== undefined) && (
                <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
                  Confidence: {Math.round((item.confidence ?? item.similarity_info?.confidence ?? 0) * 100)}%
                </div>
              )}
            </div>
            {hasSimilarItems && (
              <Tooltip title="Similar items detected">
                <ExclamationCircleOutlined style={{ color: '#ff9800', fontSize: 16 }} />
              </Tooltip>
            )}
          </div>

          {/* Tags */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {category && (
              <span style={{ 
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                border: 'none',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 500,
                display: 'inline-block'
              }}>
                {category}
              </span>
            )}
            {color && (
              <span style={{ 
                backgroundColor: '#e8f5e9',
                color: '#388e3c',
                border: 'none',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 500,
                display: 'inline-block'
              }}>
                {color}
              </span>
            )}
            {style && (
              <span style={{ 
                backgroundColor: '#f3e5f5',
                color: '#7b1fa2',
                border: 'none',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 500,
                display: 'inline-block'
              }}>
                {style}
              </span>
            )}
          </div>

          {/* Similarity Warning */}
          {hasSimilarItems && item.similarity_info && (
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                backgroundColor: '#fff3e0',
                border: '1px solid #ffb74d',
                marginBottom: 12,
                cursor: onSimilarityClick ? 'pointer' : 'default',
              }}
              onClick={() => onSimilarityClick?.(item)}
            >
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <ExclamationCircleOutlined style={{ color: '#ff9800', fontSize: 14, marginTop: 2 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#e65100', marginBottom: 4 }}>
                    Similar items detected
                  </div>
                  {item.similarity_info.message && (
                    <div style={{ fontSize: 11, color: '#f57c00', marginBottom: 4 }}>
                      {item.similarity_info.message}
                    </div>
                  )}
                  {item.similarity_info.similar_items && item.similarity_info.similar_items.length > 0 && (
                    <div style={{ fontSize: 11, color: '#ff6f00' }}>
                      Similar to: {item.similarity_info.similar_items.slice(0, 2).map((si: any) => si.name).join(', ')}
                      {item.similarity_info.similar_items.length > 2 && ` +${item.similarity_info.similar_items.length - 2} more`}
                    </div>
                  )}
                  {onSimilarityClick && (
                    <div style={{ fontSize: 11, fontWeight: 500, color: '#ff6f00', marginTop: 4 }}>
                      Click to review similar items
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            paddingTop: 8,
            borderTop: '1px solid #f0f0f0'
          }}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit?.(item)}
              style={{ 
                color: '#1976d2',
                backgroundColor: '#e3f2fd',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                height: 28
              }}
            >
              Edit Tags
            </Button>
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => onRemove?.(item)}
              danger
              style={{ 
                borderRadius: 6,
                fontSize: 12,
                height: 28
              }}
            />
          </div>
        </div>
      </div>
    </Card>
  );
};

